// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from '@insite/client-framework/SiteMessage';
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getHeader } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { ButtonWrapper } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { StyledClickable } from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import { getProp, resolveColor } from "@insite/mobius/utilities";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import PrintService from "@mcmc/App/Components/Common/PrintService";
import HeaderProvider, { McMcHeaderContext } from "@mcmc/App/Pages/Header/HeaderContext";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { getIsMoblico } from "@mcmc/App/Store/Selectors/LayoutSelectors";
import { color, layoutContainer, mergeWithCss } from "@mcmc/XDL/Utilities";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Transition } from "react-transition-group";
import { TransitionStatus } from "react-transition-group/Transition";
import styled, { css } from "styled-components";

const mapStateToProps = (state: McMcApplicationState) => {
    const logoImage = getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Basic/Logo";
    })?.fields.logoImage;

    return {
        siteName: state.context.website.name,
        session: getSession(state),
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        logoImage,
        isMoblico: getIsMoblico(state),
    };
};

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    mainContainer?: InjectableCss;
    logoTopMenus?: InjectableCss;
    navigationContainer?: InjectableCss;
    mainNavigation?: InjectableCss;
    searchInput?: InjectableCss;
    signInCartContainer?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    logoWrapper?: InjectableCss;
}

export interface StickyHeaderStyles {
    logoTopMenus: InjectableCss;
    navigationContainer: InjectableCss;
    mainContainer?: InjectableCss;
}

export const stickyHeaderStyles: StickyHeaderStyles = {
    logoTopMenus: {
        css: css`
            flex: initial;
        `,
    },
    navigationContainer: {
        css: css`
            flex: 1;
            justify-content: space-between;
        `,
    },
    mainContainer: {
        css: css`
            box-shadow: ${getProp("theme.shadows.1")};
        `,
    },
};

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, true, true, true],
        background: "navigation.primary",
    },
    container: {
        css: css`
            ${layoutContainer}
            display: flex;
            flex-wrap: wrap;
            padding-top: 15px;
            padding-bottom: 15px;

            &.moblico-header-container {
                padding-top: 0;
                padding-bottom: 0;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            position: relative;
                        `,
                    ],
                    "max",
                )}

            & > ${StyledWrapper} {
                display: flex;
                align-items: center;
            }
        `,
    },
    skipToContent: {
        skipToContent: {
            sizeVariant: "small",
            css: css`
                margin: 5px;
                position: absolute;
                clip: rect(0, 0, 0, 0);
                &:focus {
                    clip: unset;
                    z-index: 1;
                }
            `,
        },
    },
    mainContainer: {
        css: css`
            width: 100%;
            background: inherit;
            z-index: 10;
            position: relative;
            ${StyledClickable}:focus,${ButtonWrapper}:focus {
                outline-color: ${getColor("navigation.secondary.text.main")};
            }
        `,
    },
    logoTopMenus: {
        css: css`
            flex: 1 1 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [null, null, stickyHeaderStyles.logoTopMenus.css!], "max")}
        `,
    },
    navigationContainer: {
        css: css`
            flex: 1 1 100%;
            justify-content: space-between;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [null, null, stickyHeaderStyles.navigationContainer.css!], "max")}
        `,
    },
    mainNavigation: {
        css: css`
            justify-content: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            flex: 1;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    searchInput: {
        css: css`
            flex: 0 1 600px;
            ${layoutContainer}
            justify-content: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        // This is temporary until we style the auto complete input on mobile
                        null,
                        css`
                            position: absolute;
                            top: calc(-100% - 15px);
                            transition: top 350ms ease-in-out;
                            left: 0;
                            right: 0;
                            margin-top: -1px;
                            padding-top: 10px;
                            padding-bottom: 10px;
                            background-color: ${resolveColor("navigation.primary", theme)};
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    signInCartContainer: {
        css: css`
            display: flex;
            justify-content: flex-end;
            align-items: center;
            & > div {
                width: auto;
            }
        `,
    },
    websiteName: {
        css: css`
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
    logoWrapper: {
        css: css`
            margin-top: 25px;
            padding-left: 15px;
            width: 270px;
            height: 70px;
            visibility: hidden;
            position: fixed;
            top: -100%;

            img {
                max-width: 100%;
                max-height: 100%;
            }

            @media print {
                visibility: visible;
                position: static;
                width: 100%;
                ${BLUEPRINT_NAME === "mcmc" && css`
                    background-color: ${color("common.backgroundContrast")};
                    height: 90px;
                    padding: 10px 0 10px 15px;
                `}
            }
        `,
    },
};

const HeaderWrapper = styled(StyledWrapper) <{ transitionState: TransitionStatus }>`
    transition: top ease-in-out 350ms;
    ${({ transitionState }) => {
        switch (transitionState) {
            case "entering":
                return "top:-100px;";
            case "entered":
                return "position:fixed;top:0;";
            case "exiting":
            case "exited":
            default:
                return "";
        }
    }}
`;

const AlertBannerWrapper = styled(StyledWrapper) <{ isMobileSearchOpen: boolean | undefined }>`
    transition: opacity ease-in-out 350ms;
    opacity: 1;
    ${({ isMobileSearchOpen }) =>
        isMobileSearchOpen &&
        css`
            opacity: 0;
            pointer-events: none;
        `};
`;

let styles = headerStyles;

const StyledSection = getStyledWrapper("section");

let sizeObserverTimeoutId: any;
const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    logoImage,
    isMoblico,
}: Props) => {
    const [headerHeight, setHeaderHeight] = useState(0);
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    useEffect(() => {
        if (typeof window !== "undefined") {
            const queryParams = parseQueryString<{ Success?: string }>(window.location.search);
            queryParams?.["Success"] === "true" && toaster.addToast({
                body: siteMessage("McMc_PardotFormSuccessMessage"),
                messageType: "success",
                timeoutLength: 6000,
            });
        }
    }, []);

    const afterHeader = React.createRef<HTMLSpanElement>();
    const headerRef = React.createRef<HTMLDivElement>();

    useEffect(() => {
        if (headerRef.current && (window as any).ResizeObserver) {
            const resizeObserver = new (window as any).ResizeObserver((entries: any) => {
                clearTimeout(sizeObserverTimeoutId);
                sizeObserverTimeoutId = setTimeout(() => {
                    if (entries[0] && entries[0].target) {
                        setHeaderHeight(entries[0].target.clientHeight);
                    }
                }, 50);
            });

            resizeObserver.observe(headerRef.current);
            return () => {
                resizeObserver.disconnect();
            };
        }
    }, [headerRef.current]);

    return (
        <HeaderProvider>
            <McMcHeaderContext.Consumer>
                {({ isSticky, isMobileSearchOpen }) => {
                    if (isSticky) {
                        styles = mergeWithCss(headerStyles, stickyHeaderStyles);
                    } else {
                        styles = mergeWithCss(headerStyles, {});
                    }

                    return (
                        <>
                            {!isMoblico && siteName && <Typography {...styles.websiteName}>{siteName}</Typography>}
                            {!isMoblico && logoImage && (
                                <StyledSection {...styles.logoWrapper}>
                                    <img src={logoImage} alt={translate("Company Logo")} />
                                </StyledSection>
                            )}
                            <Page as="header" isHeader={true} {...styles.page}>
                                <SkipNav
                                    extendedStyles={styles.skipToContent}
                                    text={translate("Skip to main content")}
                                    destination={afterHeader}
                                />
                                <EagerLoadingLogoProvider value={true}>
                                    {!isMoblico && <Zone contentId={id} zoneName="SecondaryNavigation" />}
                                    <Transition in={isSticky} timeout={{ enter: 250, exit: 250 }}>
                                        {state => (
                                            <StyledWrapper
                                                style={{
                                                    height: headerHeight > 0 ? headerHeight : "auto",
                                                    backgroundColor: "inherit",
                                                }}
                                                id="header-wrapper"
                                            >
                                                <HeaderWrapper
                                                    {...styles.mainContainer}
                                                    transitionState={state}
                                                    ref={headerRef}
                                                >
                                                    <StyledWrapper
                                                        {...styles.container}
                                                        className={isMoblico ? "moblico-header-container" : ""}
                                                    >
                                                        {!isMoblico && (
                                                            <StyledWrapper {...styles.logoTopMenus}>
                                                                <Zone contentId={id} zoneName="LogoTopMenus" />
                                                            </StyledWrapper>
                                                        )}
                                                        <StyledWrapper {...styles.navigationContainer}>
                                                            <StyledWrapper {...styles.mainNavigation}>
                                                                <Zone contentId={id} zoneName="MainNavigation" />
                                                            </StyledWrapper>
                                                            <StyledWrapper
                                                                {...styles.searchInput}
                                                                style={{ top: isMobileSearchOpen ? "100%" : "" }}
                                                            >
                                                                <Zone contentId={id} zoneName="SearchInput" />
                                                            </StyledWrapper>
                                                            <StyledWrapper {...styles.signInCartContainer}>
                                                                <Zone contentId={id} zoneName="MyAccount" />
                                                                <Zone contentId={id} zoneName="Cart" />
                                                            </StyledWrapper>
                                                        </StyledWrapper>
                                                    </StyledWrapper>
                                                    <AlertBannerWrapper isMobileSearchOpen={isMobileSearchOpen}>
                                                        <Zone contentId={id} zoneName="AlertBanner" />
                                                    </AlertBannerWrapper>
                                                </HeaderWrapper>
                                            </StyledWrapper>
                                        )}
                                    </Transition>

                                    <Zone contentId={id} zoneName="Breadcrumb" />
                                    <span ref={afterHeader} tabIndex={-1} />
                                </EagerLoadingLogoProvider>
                            </Page>
                        </>
                    );
                }}
            </McMcHeaderContext.Consumer>
            <PrintService />
        </HeaderProvider>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
